<template>
  <div >
    <div v-if="!hasData" class="nodata  col-12" >
        No data available
      </div>
      <div v-if="hasError" class="haserror  col-12" >
        {{error}}
      </div>
    <div class="" style="overflow-y:auto;overflow-x:clip">
      <div class="" id="nav-today">
        <div class="flex-fill  rounded-3 bg-light mb-3 row" style="min-height:450px">

          <div class="col-12 col-md-6 " >
              <div class="weatherMap rounded mh-sm-50" ></div>
          </div>
        
          <div class="col-12 col-md-6 mt-md-2 mt-3 d-flex flex-column">
              <div class="position-relative mt-3 order-2 order-md-1" style="height:200px;">
                <div class="chartcontainer reflectivitycontainer h-100">
                  <svg class="chart"></svg>
                  <div class="periods periods overflow-hidden">
                    <template v-for="(period,index) in minutely">
                      <div  class="period">
                        <div class="time">{{formatTime(period)}}</div>
                      </div>
                    </template>
                  </div>
                </div>
                <!--<div class="fs-xs position-absolute end-0 top-0 z-3 pe-4 pt-1">{{ mapRainRate }}</div> -->
              </div>

              <div class="fs-sm mt-2 order-3 order-md-2" v-html="nearbyRainWithNone"></div>

              <!-- Map Time Controls -->
              <div class="d-flex align-items-center py-1 justify-content-between bg-light rounded order-1 order-md-4">
                <div class="d-flex align-items-center justify-content-between gap-2 flex-grow-1 mx-2">

                  
                  <!-- Step backwards -->
                  <button class="btn btn-sm btn-secondary d-none d-sm-inline" @click="stepTime(-60)" :disabled="isPlaying">
                    -1hr <i class="bi bi-skip-backward-fill"></i> 
                  </button>
                  <button class="btn btn-sm btn-secondary" @click="stepTime(-activeLayerInterval)" :disabled="isPlaying">
                    -{{activeLayerInterval}}m <i class="bi bi-chevron-left"></i> 
                  </button>
                  
                  <!-- Current time display -->
                  <div class="px-2 text-center" style="min-width: 100px;">
                    <div class="fw-bold">{{ mapDateTime ? formatTime({ datetimeEpoch: mapDateTime.getTime() / 1000 }) : '--:--' }}</div>
                    <div class="fs-xxs text-muted">{{ mapDateTime ? formatDate({ datetimeEpoch: mapDateTime.getTime() / 1000 }, true) : '' }}</div>
                  </div>
                  
                  <!-- Step forwards -->
                  <button class="btn btn-sm btn-secondary" @click="stepTime(activeLayerInterval)" :disabled="isPlaying">
                    +{{activeLayerInterval}}m <i class="bi bi-chevron-right"></i>
                  </button>
                  <button class="btn btn-sm btn-secondary d-none d-md-inline" @click="stepTime(60)" :disabled="isPlaying">
                    +1hr <i class="bi bi-skip-forward-fill"></i>
                  </button>
                  
                  <!-- Reset to now -->
                  <button class="btn btn-sm btn-warning" @click="resetToNow" :disabled="isPlaying">
                    <i class="bi bi-arrow-clockwise"></i> Reset
                  </button>

                  <!-- Play/Pause -->
                  <button class="btn btn-sm btn-primary" @click="playPause" :class="{ 'btn-primary': isPlaying }">
                    <i class="bi" :class="isPlaying ? 'bi-pause-fill' : 'bi-play-fill'"></i>
                  </button>
                </div>

                
                <!-- Speed control -->
                <div class="d-flex flex-column justify-content-between align-items-center">
                  <label class="fs-xxs text-muted mb-0">Speed:</label>
                  <select class="form-select form-select-sm" style="width: auto;" v-model.number="playSpeed" :disabled="isPlaying">
                    <option :value="2000">0.5x</option>
                    <option :value="1000">1x</option>
                    <option :value="500">2x</option>
                    <option :value="250">4x</option>
                  </select>
                </div>
              </div>

            </div>

            <div v-if="showCurrent" class="rounded-3 bg-light mt-3 mx-1">
              <h5 class="col-12">Current Conditions</h5>
              <div v-if="showAlerts" class="d-flex flex-column mb-3">
                
                <div class="d-flex flex-wrap gap-2 position-relative fs-6">
                  <span 
                    v-for="(a,i) in alerts" 
                    :key="i"
                    @click="toggleAlertExpand(a)"
                    class="badge cursor-pointer"
                    :class="getAlertBadgeClass(a)"
                    style="cursor: pointer;"
                  >
                    <i class="me-1" :class="getAlertIconClass(a)"></i>{{ a.event }}
                  </span>
                  <template v-if="expandedAlert">
                    <div class="modal-backdrop fade show" @click="expandedAlert = null"></div>
                    
                    <div class="modal fade show"  style="display: block;" tabindex="-1"  aria-modal="true" role="dialog">
                    
                      <div class="modal-dialog modal-dialog-centered" 
                          style="max-width:calc(100vw - 20px); max-height:calc(100vh - 20px);">
                        <div class="modal-content" style="max-height:calc(100vh - 20px); overflow:auto;">
                          <div class="modal-header">
                            <h6 class="modal-title mb-0">{{ expandedAlert.headline }}</h6>
                            <button type="button" class="btn-close" @click="expandedAlert = null"></button>
                          </div>
                          <div class="modal-body">
                            <p class="mb-0 small" v-html="alertDesc(expandedAlert)"></p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>

              <div class="d-flex align-items-center mb-5 pe-2">
                <!-- Icon and Time - Fixed width -->
                <div class="d-flex align-items-center flex-shrink-0 me-3">
                  <div class="icon me-2">
                    <div class="" :class="currentConditions.icon"></div>
                  </div>
                  <div class="text-nowrap">{{formatTime(currentConditions)}}</div>
                </div>

                <!-- Data columns - Flexible width with overflow handling -->
                <div class="flex-grow-1 overflow-hidden ">
                  <div class="d-flex gap-1  justify-content-between">
                    <!-- Temperature -->
                    <div class="weather-stat">
                      <div class="fs-xxs opacity-70">Temp</div>
                      <span v-if="currentConditions.feelslike !== currentConditions.temp" class="text-nowrap">
                        <span v-html="formattedValue(currentConditions,'temp')" /> (<span v-html="formattedValue(currentConditions,'feelslike')" />)
                      </span>
                      <span v-else v-html="formattedValue(currentConditions,'temp')" />
                    </div>

                    <!-- Precipitation -->
                    <div class="weather-stat">
                      <div class="fs-xxs opacity-70">Precip</div>
                      <span v-if="currentConditions.snow" class="fs-xxs text-nowrap">
                        <i class="bi bi-snow"></i>{{ formattedValue(currentConditions, 'snow') }}
                      </span>
                      <span v-else-if="currentConditions.precipremote >= 0.005" class="fs-xxs text-nowrap">
                        <i class="bi bi-broadcast"></i>{{ formattedValue(currentConditions, 'precipremote', false, 'precip') }}
                      </span>
                      <span v-else-if="currentConditions.precip >= 0.005" class="fs-xxs text-nowrap">
                        <i class="bi bi-droplet"></i>{{ formattedValue(currentConditions, 'precip', false) }}
                      </span>
                      <span v-else class="text-nowrap">-</span>
                    </div>

                    <!-- UVI -->
                    <div class="weather-stat">
                      <div class="fs-xxs opacity-70">UVI</div>
                      <div v-html="formattedValue(currentConditions,'uvindex2')"></div>
                    </div>

                    <!-- Wind -->
                    <div class="weather-stat">
                      <div class="fs-xxs opacity-70">Wind</div>
                      <div class="d-flex justify-content-center" :title="formattedValue(currentConditions,'wind')">
                        <div class="position-relative" style="width:15px">
                          <div class="position-absolute" :style="'transform:rotate('+Math.round(currentConditions.winddir)+'deg)'">
                            <i class="bi bi-arrow-down"></i>
                          </div>
                        </div>
                        <span class="text-nowrap" v-html="formattedValue(currentConditions,'windspeed', true)" />
                      </div>
                    </div>

                    <!-- Visibility - Hide on small screens -->
                    <div class="weather-stat d-none d-sm-block">
                      <div class="fs-xxs opacity-70">Visibility</div>
                      <div v-html="formattedValue(currentConditions,'visibility')"></div>
                    </div>

                    <!-- AQI -->
                    <div class="weather-stat">
                      <div class="fs-xxs opacity-70">AQI</div>
                      <div class="d-flex justify-content-center align-items-center">
                        <span class="text-nowrap">{{formattedValue(currentConditions,'aqi',true)}}</span>
                        <!-- Tooltip/popup for AQI element when AQI > 50 -->
                         <!--
                        <div v-if="currentConditions.aqius > 50" class="position-relative">
                          <i class="bi bi-info-circle ms-1" 
                            style="cursor: pointer; font-size: 0.75rem;"
                            @mouseenter="aqiPopupOpen = 'current'"
                            @mouseleave="aqiPopupOpen = null"
                          ></i>
                          <div v-if="aqiPopupOpen === 'current'" 
                              class="position-absolute bottom-100 start-50 translate-middle-x bg-light border rounded shadow p-2 mb-1"
                              style="z-index: 1000; white-space: nowrap;">
                            <div class="fs-xxs">Primary pollutant:</div>
                            <div class="fw-bold">{{formattedValue(currentConditions,'aqielement')}}</div>
                          </div>
                        </div>
                        -->
                      </div>
                    </div>
                  </div>
                </div>
              </div>
 
            </div>

        </div>




       
        <div v-if="today" class="mt-3  mx-1 ">
          <h5 class="col-12">Today's Forecast</h5>
          <DayWidget v-for="(day,dayIndex) in daysShortTerm" 
            :key="day.datetimeEpoch"
            :days="daysShortTerm" 
            :day="day" 
            :timezone="wxdata.timezone" 
            :unitGroup="unitGroup" 
            :class="{'border-bottom': dayIndex < daysShortTerm.length - 1}"
            class="border-dark-subtle" />
          <!--
          <DayWidget :days="days" :day="today" :timezone="wxdata.timezone" :unitGroup="unitGroup" />-->
        </div>
        
        
        <div v-if="yesterday" class="rounded-3  mt-3  mx-1 ">
          <h5 class="col-12">Yesterday's conditions</h5>
          <DayWidget :days="days" :day="yesterday" :timezone="wxdata.timezone" :unitGroup="unitGroup" />
        </div>


        <div class="mt-3  mx-1 ">
           <h5 v-if="isForecastNextDays" class="col-12">Daily forecast</h5>
           <h5 v-else class="col-12">Daily weather data</h5>
          <DayWidget v-for="(day,dayIndex) in nextDays" :days="nextDays" :day="day" :timezone="wxdata.timezone" :unitGroup="unitGroup"
          :class="{'border-bottom': dayIndex < nextDays.length - 1}"
                  class="border-dark-subtle" 
                   />
        </div>
      </div>


      <div class="tab-pane" id="nav-hourly">
         <!--<div v-for="(day,i) in days" class="row" :id="'hourDay'+i">-->
         
          <div v-if="selectedDay" class="row">
              <div class="col-11 h6">{{formatDate(selectedDay)}}</div>
              <div v-for="(hour,i) in selectedDay.hours" class="row  pb-1 mb-1 fs-xs border-bottom border-dark-subtle">
                
                <div class="col-2 d-flex flex-column align-items-center justify-content-center">
                  <div class="icon me-2"><div class="" :class="hour.icon"></div></div>
                  <div class="fs-xxs">{{formatTime(hour)}}</div>
                </div>
                <div class="col-10 row">
                  <div class="col-3 col-md-2 text-center">
                    <div class="" v-html="formattedValue(hour,'temp')"></div>
                    <div v-if="hour.feelslike!==hour.temp" class="fs-xxs">Feels:<span v-html="formattedValue(hour,'feelslike')" /></div>
                </div>
                
                  
                <div class="col-4 col-md-2 text-center">
                  <div class="fs-xxs opacity-70">Precip</div>
                  <div class="">{{formattedValue(hour,'precipprob')}}</div>
                  <div v-if="hour.precip" class="fs-xxs d-flex justify-content-center"><i class="d-none d-sm-block  bi bi-droplet" ></i>{{formattedValue(hour,'precip')}}</div>
                  <div v-if="hour.snow" class="fs-xxs  d-flex justify-content-center"><i class="d-none d-sm-block bi bi-snow" ></i>{{formattedValue(hour,'snow')}}</div>
                </div>
                <div class="col-4 col-md-1 fs-xs text-center">
                  <div class="fs-xxs opacity-70">UVI</div>
                  <div class="" v-html="formattedValue(hour,'uvindex2')"></div>
                </div>
                 
                <div class="col-4 col-md-2 text-center">
                  <div class="fs-xxs opacity-70">Wind</div>
                  <div class="d-flex justify-content-center" :title="formattedValue(hour,'wind')">
                    <div class="position-relative"  style="width:15px" >
                      <div class="position-absolute"  :style="'transform:rotate('+Math.round(hour.winddir)+'deg)'"><i class="bi bi-arrow-down" ></i></div>
                    </div>
                    <span class="" v-html="formattedValue(hour,'windspeed',true)" />
                  </div>
                  <div class="d-none d-md-block fs-xxs" v-html="formattedValue(hour,'pressure')"></div>
                </div>
                <div class="col-4 col-md-2  text-center">
                  <div class="fs-xxs opacity-70">Humidity</div>
                  <div class="" v-html="formattedValue(hour,'humidity')"></div>
                  <div class="d-none d-md-block fs-xxs" v-html="formattedValue(hour,'dew')"></div>
                </div>
                
                <div class="col-4 col-md-3 text-center">
                  <div class="fs-xxs opacity-70">AQI</div>
                  <div class="d-none d-md-block">{{formattedValue(hour,'aqi')}}</div>
                  <div class="d-block d-md-none">{{formattedValue(hour,'aqi',true)}}</div>
                  <!-- Tooltip/popup for AQI element when AQI > 50 -->
                  <div v-if="hour.aqius > 50" class="position-relative d-inline-block">
                    <i class="bi bi-info-circle-fill text-warning" 
                       style="cursor: pointer; font-size: 0.75rem;"
                       @click="toggleAqiPopup('hour-' + i)"
                    ></i>
                    <div v-if="aqiPopupOpen === 'hour-' + i" 
                         class="position-absolute bottom-100 start-50 translate-middle-x bg-light border rounded shadow p-2 mb-1"
                         style="z-index: 1000; white-space: nowrap;">
                      <button type="button" class="btn-close btn-sm float-end" @click="aqiPopupOpen = null"></button>
                      <div class="fs-xxs">Primary pollutant:</div>
                      <div class="fw-bold">{{formattedValue(hour,'aqielement')}}</div>
                    </div>
                  </div>
                </div>
              </div>
                  
            </div>
          </div>
          
            
      </div>

    </div>

  </div>
</template>
<script>
import '/src/weather-map/WxTileLayer.js';
import '/src/weather-map/WxLegend.js';
import {VcWidgetHelper} from './common_widget.js'
import {VcClipboardUtils} from '/src/visualcrossing-core/vcclipboardutils.js'
import {VcGlobal} from "/src/visualcrossing-core/vcglobal_conn.js";
import { getAlertByType, getAlertClass, getAlertIcon } from '/src/data/weatherAlerts.js';
import * as d3 from "d3";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import DayWidget from './day-widget.vue';

export default {
  name: 'day-summary-widget2',
  props:{
    weatherData:Object,
    parentData:Boolean,
    config:Object,
    newLocation:String,
  },
  emits: ['update:location'],
  data() {
    return {
      id:null,
      location:"?",
      name:null,
      key:null,
      startdate:null,
      enddate:null,
      unitGroup:null,
      wxdata:this.weatherData,
      error:null,
      include:null,
      options:null,
      forecastBasisDate:null,
      forecastBasisDay:null,
      viewincludes:null,
      summarykpis:[],
      dayIndex:0,
      endDayIndex:0,
      hourIndex:null,
      period:"day",
      rooturl:null,
      widgetcolumncount:1,
      showMapFullScreen:false,
      selectedDay:null,
      updateMapTimeLine:null,
      mapDateTime:null,
      isPlaying: false,
      playInterval: null,
      playSpeed: 1000, // milliseconds between frames
      expandedAlert: null, // Change from array to single alert
      aqiPopupOpen: null, // Track which AQI popup is open
      activeLayerInterval: 10, // Default interval in minutes
    }
  },
  methods:{

    findInitialMapDateTime() {
      const me = this;

      let initialTime;
      if (!me.wxdata) {
        initialTime = new Date();
      } else if (me.today) {
        initialTime = new Date();
      } else {
        let day1 = me.days && me.days.length ? me.days[0] : null;
        initialTime = day1 ? new Date(day1.datetimeEpoch * 1000 + 12 * 60 * 60 * 1000) : new Date();
       
      } 
      console.log("Initial map time:", initialTime);
      return me.toEpochDateInstance(initialTime.getTime()/1000);
    },

      // Initialize mapDateTime when data loads
    initializeMapDateTime() {
      const me = this;
      //if (!me.wxdata || !me.minutely.length) return;
      
      // Get active layer interval
      me.activeLayerInterval = me.getActiveLayerInterval();
      
      // Set to current time by default
      let initialTime = me.findInitialMapDateTime();
      
      // Or set to the first precipitation event
      //const firstPrecip = me.minutely.find(m => m.reflectivity > 0);
      //if (firstPrecip) {
     //   initialTime = new Date(firstPrecip.datetimeEpoch * 1000);
      //}
      
      // Round to interval
      initialTime = me.roundToInterval(initialTime, me.activeLayerInterval);
      console.log("Setting initial map time to:", initialTime); 
      me.mapDateTime = initialTime;
      
      // Update map if it exists
      if (me.leafletMap) {
        me.leafletMap.options.dateTime = me.mapDateTime;
        me.leafletMap.options.dateTimeInterval = me.activeLayerInterval;
        me.leafletMap.fire('datetimeupdated', { dateTime: me.mapDateTime });
      }
    },
    
    // Time control methods
    setMapDateTime(datetime) {
      const me = this;
      
      // Round to active layer interval
      const interval = me.activeLayerInterval;
      const roundedDateTime = me.roundToInterval(datetime, interval);
      
      me.mapDateTime = roundedDateTime;
      
      // Update map
      if (me.leafletMap) {
        me.leafletMap.options.dateTime = roundedDateTime;
        me.leafletMap.options.dateTimeInterval = interval; // Update this too
        
        // Trigger update for all layers
        if (me.wxLayers) {
          Object.values(me.wxLayers).forEach(layer => {
            if (me.leafletMap.hasLayer(layer)) {
              layer.options.dateTime = roundedDateTime;
              layer.redraw();
            }
          });
        }
      }
      
      // Update chart timeline
      if (me.updateMapTimeLine) {
        me.updateMapTimeLine(roundedDateTime);
      }
    },
    
    // Round datetime to the nearest interval
    roundToInterval(datetime, intervalMinutes) {
      const ms = datetime.getTime();
      const intervalMs = intervalMinutes * 60 * 1000;
      return new Date(Math.floor(ms / intervalMs) * intervalMs);
    },
    
    // Get the current active layer's interval
    getActiveLayerInterval() {
      const me = this;
      if (!me.leafletMap) return 10;
      
      let activeInterval = 10; // Default
      
      // Check all layers to find the active one
      if (me.wxLayers) {
        Object.values(me.wxLayers).forEach(layer => {
          if (me.leafletMap.hasLayer(layer) && layer.options && layer.options.dateTimeInterval) {
            activeInterval = layer.options.dateTimeInterval;
          }
        });
      }
      
      return activeInterval;
    },
    
    stepTime(minutes) {
      const me = this;
      if (!me.mapDateTime) me.initializeMapDateTime();
      
      // Use the active layer's interval for stepping
      const stepInterval = minutes || me.activeLayerInterval;
      
      const newTime = new Date(me.mapDateTime.getTime() + stepInterval * 60 * 1000);
      
      // Constrain to available data range
      const minTime = me.minutely[0]?.datetimeEpoch * 1000;
      const maxTime = me.minutely[me.minutely.length - 1]?.datetimeEpoch * 1000;
      
      if (newTime.getTime() < minTime) {
        newTime.setTime(minTime);
      } else if (newTime.getTime() > maxTime) {
        newTime.setTime(minTime);
      }
      
      me.setMapDateTime(newTime);
    },
    
    
    playPause() {
      const me = this;
      me.isPlaying = !me.isPlaying;
      
      if (me.isPlaying) {
        // Start playing
        me.playInterval = setInterval(() => {
          console.log('Playing:', me.mapDateTime);
          me.stepTime(); // Use active interval
          
          
        }, me.playSpeed);
      } else {
        me.stopPlaying();
      }
    },
    
    stopPlaying() {
      const me = this;
      me.isPlaying = false;
      if (me.playInterval) {
        clearInterval(me.playInterval);
        me.playInterval = null;
      }
    },
    
    resetToNow() {
      const me = this;
      me.stopPlaying();
      let initialTime = me.findInitialMapDateTime();
      me.setMapDateTime(initialTime);
    },

    getAlertBadgeClass(alert) {
      const alertInfo = getAlertByType(alert.event);
      const alertClass = alertInfo ? getAlertClass(alertInfo.category) : 'alert-warning';
      
      // Convert Bootstrap alert classes to badge classes
      const badgeClassMap = {
        'alert-danger': 'bg-danger',
        'alert-warning': 'bg-warning text-dark',
        'alert-info': 'bg-info text-dark'
      };
      
      return badgeClassMap[alertClass] || 'bg-warning text-dark';
    },
    
    getAlertIconClass(alert) {
      const alertInfo = getAlertByType(alert.event);
      return alertInfo ? getAlertIcon(alertInfo.category) : 'bi-exclamation-circle-fill';
    },
    toggleAlertExpand(alert) {
      // Toggle - if same alert clicked, close it; otherwise open new one
      this.expandedAlert = this.expandedAlert === alert ? null : alert;
    },
    
    isAlertExpanded(alert) {
      return this.expandedAlert === alert;
    },

    getAlertId(alert) {
      // Create a unique identifier for the alert
      // Using a combination of event name and start time to ensure uniqueness
      return `${alert.event}_${alert.onset || alert.start || ''}`;
    },
    isExpanded(day){
      let me=this;
      return day===me.selectedDay;
    },
    selectDay(day) {
      let me=this;
      me.selectedDay=me.isExpanded(day)?null:day;
    },



    refreshData(forceRefresh) {
      var me=this;
      if (me.parentData) {
        me.initializeMapDateTime();
        return;
      }
      VcWidgetHelper.populateData( {
                    location:me.location,
                    startdate:me.startdate,
                    enddate:me.enddate,
                    key:me.key,
                    unitgroup:me.unitGroup,
                    elements:me.elements,
                    include:me.include,
                    options:me.options,
                    forecastBasisDate:me.forecastBasisDate,
                    forecastBasisDay:me.forecastBasisDay,
                    id:me.id,
                    forceRefresh:forceRefresh || me.forceRefresh,
                })
      .then(response => {
          me.wxdata=response.data;
          if (me.wxdata.unitGroup ) me.unitGroup=me.wxdata.unitGroup;
          if (!me.location) me.location=me.wxdata.resolvedAddress;
          me.initializeMapDateTime();
          me.updateMap();
          me.renderCharts();
      }).catch((error) => {
          console.error("Error loading weather data:", error);
           me.error=(error && error.msg) || "An unknown error occurred";
      });
    },
    formatNormalValue(period, element, index) {
        let me=this;
        if (!period || !period.normal) return "?";
        let normals;
        if (element==="rainsnow") {
          if (period.snow>0) {
            normals= period.normal["snow"];
          } else {
            normals= period.normal["precip"];
          }
        } else {
          normals=period.normal[element];
        }

        return VcWidgetHelper.formatElementValue(normals[index], element,me.unitGroup,null,"",false,true );
    },
    formattedValue(period, element, shortForm, formatElement) {
        var me=this;
        formatElement=formatElement || element;
        if (element==="rainsnow") {
          if (period.snow>0) return me.formatValue(period.snow, "snow");
          return me.formatValue(period.precip, "precip");
        }
        if (element==="aqi") return me.formattedAqi(period,shortForm);
        if (element==="aqielement") return me.formattedAqiElement(period);
        if (element==="wind") return me.formattedWind(period);
        if (element==="moonphase") return me.formattedMoonPhase(period);
        if (element==="sunriseset") return me.formattedSunriseset(period);
        return me.formatValue(period[element], formatElement, shortForm);
    },
    formattedAqi(period,shortForm) {
      let aqi=period.aqius;
      if (!aqi && aqi!==0) return "-";
      //return `${VcWidgetHelper.getAqiText(aqi,shortForm)} (${this.formatValue(aqi, "aqius")})`
      return `${VcWidgetHelper.getAqiText(aqi,shortForm)}`
    },
    formattedAqiElement(period) {
      let aqielement=period.aqielement;
      let aqi=period.aqius;
      if (!aqielement) return  `(${this.formatValue(aqi, "aqius")})`;
      return `${VcWidgetHelper.getAqiElementText(aqielement)}  (${this.formatValue(aqi, "aqius")})`;
    },
    formattedWind(period) {
      return VcWidgetHelper.getCompassDirection(period.winddir)+" "+
      this.formatValue(period.windspeed, "wind");
    },
    formattedMoonPhase(period) {
      return VcWidgetHelper.getMoonPhaseDesc(period.moonphase);
    },
    formattedSunriseset(period) {
      return  this.formatValue(period.sunrise)+"-"+this.formatValue(period.sunset);
    },
    formatValue(value, element, shortForm) {
      var me=this;
      const fixed=shortForm?0:null;
      const emptyvalue=null, unicode=null;
      const noSymbol=shortForm;
      return VcWidgetHelper.formatElementValue(value, element,me.unitGroup,   fixed, emptyvalue,  unicode, noSymbol );
    },
    formatTime(period, property) {
      if (!period) return "-";
      const value=period && property && period[property]? period[property]:period.datetimeEpoch;
      return VcWidgetHelper.formatTime(value, this.wxdata.timezone);
    },
    formatDate(period,shortForm) {
      if (!period) return "-";

      if (VcWidgetHelper.isYesterday(period.datetimeEpoch, this.wxdata.timezone)) return "Yesterday";
      if (VcWidgetHelper.isToday(period.datetimeEpoch, this.wxdata.timezone)) return "Today";


      if (shortForm)  return VcWidgetHelper.formatDateVeryShort(period.datetimeEpoch, this.wxdata.timezone);
      return VcWidgetHelper.formatDateShort(period.datetimeEpoch, this.wxdata.timezone);
    },
    doShow(element, hideByDefault) {
      if (hideByDefault && !this.viewincludes) return false;
      return !this.viewincludes || this.viewincludes.indexOf(element)!==-1;
    },
    alertDesc(alert) {
      var desc= alert && alert.description;
      desc=(desc && desc.replace(/(\*)/g, '<br>')) || "";
      return desc;
    },
    updateMap() {
      let me=this;
      if (!me.leafletMap) return;

      if (!me.wxdata) return;
      let zoom=me.leafletMap.getZoom();
      if (zoom<7) {
        zoom=7;
      }

      me.leafletMap.setView([me.wxdata.latitude, me.wxdata.longitude], zoom);
      if (me.circleMarker) me.circleMarker.remove();
      me.circleMarker = L.circleMarker([me.wxdata.latitude, me.wxdata.longitude], {
            color: 'blue',
            fillColor: 'blue',
            fillOpacity: 0.5,
            radius: 3,
            weight:1,
        }).addTo(me.leafletMap);


    },
    
    renderMap(retries) {
      var me=this;
      var containerElement=me.$el.parentElement;
      var mapElement=containerElement.querySelector(".weatherMap");

      if (!mapElement) {
        retries=retries || 0;
        if (retries<5) {
          me.$nextTick(
            function() {
              me.renderMap(retries++);
            }
          )
        } else {
          console.error("Could not find map element after 5 tries");
        }
        return;
      }


      if (!L) {
        console.error("No Leaflet found");
        return;
      }
      if (!me.leafletMap) {
        me.leafletMap = new L.Map(mapElement, {
          fadeAnimation:false,
          minZoom: 1,
          maxZoom: 16,
          center: [45, -103], 
          zoom:4,
          dateTime: me.mapDateTime || null, // Use the component's mapDateTime
          dateTimeInterval: me.activeLayerInterval,
        });

        const resizeObserver = new ResizeObserver(() => {
          me.leafletMap.invalidateSize();
        });

        resizeObserver.observe(mapElement);


        let legend= new L.Control.WxLegend({ position: "bottomleft", showDateTimeControl:false }).addTo(me.leafletMap);
        /*
        var Stamen_TonerBackground = L.tileLayer('https://stamen-tiles-{s}.a.ssl.fastly.net/toner-background/{z}/{x}/{y}{r}.{ext}', {
           // attribution: 'Map tiles by <a href="http://stamen.com">Stamen Design</a>, <a href="http://creativecommons.org/licenses/by/3.0">CC BY 3.0</a> &mdash; Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            subdomains: 'abcd',
              opacity:0.4,
            minZoom: 0,
            maxZoom: 20,
            ext: 'png'
          });


        me.leafletMap.addLayer(Stamen_TonerBackground);

        */
        var CartoDB_PositronNoLabels = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
          subdomains: 'abcd',
          maxZoom: 20
        });

        me.leafletMap.addLayer(CartoDB_PositronNoLabels);
        
        let forceRefresh=true;
       
        
        
       
        const tempLayer=new L.TileLayer.WxTileLayer({
            opacity:0.6,
            level:0,
            model:null,
            element: "temp",
            forceRefresh:forceRefresh,
            key:VcGlobal.MAP_TILE_KEY,
            unitGroup:me.unitGroup,
            dateTimeInterval:60,
           // datetime:tileDateTime,
        });


        const cloudcoverLayer=new L.TileLayer.WxTileLayer({
            opacity:0.6,
            level:0,
            model:null,
            element: "cloudcover",
            forceRefresh:forceRefresh,
            key:VcGlobal.MAP_TILE_KEY,
            unitGroup:me.unitGroup,
            dateTimeInterval:60,
           // datetime:tileDateTime,
        });


        const sealevelpressureLayer=new L.TileLayer.WxTileLayer({
            opacity:0.6,
            level:0,
            model:null,
            element: "sealevelpressure",
            forceRefresh:forceRefresh,
            key:VcGlobal.MAP_TILE_KEY,
            unitGroup:me.unitGroup,
            dateTimeInterval:60,
           // datetime:tileDateTime,
        });


        const windLayer=new L.TileLayer.WxTileLayer({
            opacity:0.6,
            level:0,
            model:null,
            element: "wind",
            forceRefresh:forceRefresh,
            key:VcGlobal.MAP_TILE_KEY,
            unitGroup:me.unitGroup,
            dateTimeInterval:60,
           // datetime:tileDateTime,
        });

        const alertsLayer=new L.TileLayer.WxTileLayer({
            opacity:0.6,
            level:0,
            model:null,
            element: "alerts",
            forceRefresh:forceRefresh,
            key:VcGlobal.MAP_TILE_KEY,
            unitGroup:me.unitGroup,
            dateTimeInterval:60,
           // datetime:tileDateTime,
        });

        const precipcompositeLayer=new L.TileLayer.WxTileLayer({
            opacity:0.6,
            level:0,
            model:null,
            element: "precipcomposite",
            forceRefresh:forceRefresh,
            key:VcGlobal.MAP_TILE_KEY,
            unitGroup:me.unitGroup,
            dateTimeInterval:5,
           // datetime:tileDateTime,
        });

        me.leafletMap.addLayer(precipcompositeLayer);

        
        const preciprateLayer=new L.TileLayer.WxTileLayer({
            opacity:0.6,
            level:0,
            model:null,
            element: "preciprate",
            forceRefresh:forceRefresh,
            key:VcGlobal.MAP_TILE_KEY,
            unitGroup:me.unitGroup,
            dateTimeInterval:5,
           // datetime:tileDateTime,
        });

        const preciptypeLayer=new L.TileLayer.WxTileLayer({
            opacity:0.6,
            level:0,
            model:null,
            element: "preciptype",
            forceRefresh:forceRefresh,
            key:VcGlobal.MAP_TILE_KEY,
            unitGroup:me.unitGroup,
            dateTimeInterval:5,
           // datetime:tileDateTime,
        });

        const hailsizeLayer=new L.TileLayer.WxTileLayer({
            opacity:0.6,
            level:0,
            model:null,
            element: "hailsize",
            forceRefresh:forceRefresh,
            key:VcGlobal.MAP_TILE_KEY,
            unitGroup:me.unitGroup,
            dateTimeInterval:5,
           // datetime:tileDateTime,
        });

        me.wxLayers = {


            "Alerts": alertsLayer,
            "Precip Composite": precipcompositeLayer,
            "Precip Rate": preciprateLayer,
            
            "Precip Type": preciptypeLayer,
            "Hail Size": hailsizeLayer,
            
            "Temp": tempLayer,
            "Cloud Cover": cloudcoverLayer,
            "Pressure": sealevelpressureLayer,
            "Wind": windLayer,
           

        };

       

        // Add layer control events to update activeLayerInterval
        me.leafletMap.on('overlayadd', function(e) {
          // Layer was added
          const layer = e.layer;
          if (layer.options && layer.options.dateTimeInterval) {
            me.activeLayerInterval = layer.options.dateTimeInterval;
            
            // Re-round current time to new interval
            if (me.mapDateTime) {
              me.setMapDateTime(me.mapDateTime);
            }
          }
        });

        // Also listen for baselayerchange if you have base layers
        me.leafletMap.on('baselayerchange', function(e) {
          const layer = e.layer;
          if (layer.options && layer.options.dateTimeInterval) {
            me.activeLayerInterval = layer.options.dateTimeInterval;
            
            // Re-round current time to new interval
            if (me.mapDateTime) {
              me.setMapDateTime(me.mapDateTime);
            }
          }
        });

        var layerControl = L.control.layers(null, me.wxLayers).addTo(me.leafletMap);
        
        // Set initial activeLayerInterval based on the first active layer
        me.activeLayerInterval = me.getActiveLayerInterval();
        me.leafletMap.on('contextmenu', function (e) {
          let contextMenu=L.DomUtil.create('div','d-flex flex-column');

          let copyLatLong=L.DomUtil.create('a','nav-link',contextMenu);
          let openLocation=L.DomUtil.create('a','nav-link',contextMenu);
          copyLatLong.innerText="Copy Lat,Lon";
          openLocation.innerText="Show ";

          let popup = L.popup({closeButton:false})
            .setLatLng(e.latlng)
            .setContent(contextMenu)
           
          let location=(Math.round(e.latlng.lat*10000)/10000)+","+(Math.round(e.latlng.lng*10000)/10000);
           
          
          L.DomEvent.on(copyLatLong, "click", function(e) {
            VcClipboardUtils.copyToClipboard(location);
            popup.setContent("Copied!")
            setTimeout(function() {me.leafletMap.closePopup(popup)}, 2000);
          });

          L.DomEvent.on(openLocation, "click", function(e) {
             me.$emit('update:location', location);
             setTimeout(function() {me.leafletMap.closePopup(popup)}, 1000);
          });




        
          popup.openOn(me.leafletMap);
           me.leafletMap.on('click', function (e) {
          //  me.updateWxDetail(e);
        });

        me.leafletMap.on('dblclick', function (e) {
          if (!e || !e.originalEvent || !e.originalEvent.ctrlKey) return;
          let location=(Math.round(e.latlng.lat*10000)/10000)+","+(Math.round(e.latlng.lng*10000)/10000);
          me.newLocation=location;
          me.setLocation();
          L.DomEvent.stopPropagation(e);
        });

        });      }

      me.mapDateTime =  me.leafletMap.options.dateTime;
    },
    toEpochDateInstance(epochTime, tzoffset) {
      var me=this;
      var currentTzoffset=new Date().getTimezoneOffset()*60000;
      tzoffset=(tzoffset?tzoffset:me.wxdata.tzoffset);
      return new Date((epochTime*1000-tzoffset*3600000-currentTzoffset));
    },
    toLocalDateInstance(epochTime, tzoffset) {
      var me=this;
      var currentTzoffset=new Date().getTimezoneOffset()*60000;
      tzoffset=(tzoffset?tzoffset:me.wxdata.tzoffset);
      return new Date(epochTime*1000+tzoffset*3600000+currentTzoffset);
    },
    renderCharts() {
      console.log("renderCharts");
      var me=this;

      //window.addEventListener("resize", renderCharts);
      /*
            loadD3().then(d3=>{

              if (!window.d3) window.d3=d3;
              
              var reflectivitycontainer = d3.select(this.$el).select(".reflectivitycontainer");

              me.renderReflectivityChart(reflectivitycontainer);



            })
      */
    var reflectivitycontainer = d3.select(this.$el).select(".reflectivitycontainer");

    me.renderReflectivityChart(reflectivitycontainer);

      
    },

    renderReflectivityChart(container) {

      console.log("renderReflectivityChart");
      var me=this;
      var data=me.minutely;

      if (!data.length) {
        console.log("no data");
        return;
      }
      
      const current=new Date();
      

      


      var currentLocal=me.toLocalDateInstance(current.getTime()/1000);

    

      data.reduce(function(a,d) {
            d.datetimeInstance=me.toLocalDateInstance(d.datetimeEpoch, d.tzoffset); //new Date(d.datetimeEpoch*1000+(d.tzoffset?d.tzoffset:me.wxdata.tzoffset)*3600000+tzoffset);
            return a;
        }, {
           
      });


      var chartContainer=container.select(".chart");

      if (chartContainer.empty()) {
        console.log("no chart container found");
        return;
      }

      
      // Fix 1: Get the parent container dimensions instead of the SVG
      var containerNode = container.node();
      var rect = containerNode.getBoundingClientRect();
      
      // Fix 2: Set explicit dimensions on the SVG
      var margin = {top: 0, right: 20, bottom: 15, left: 15};
      var width = rect.width - (margin.left + margin.right);
      var height = 200 - (margin.top + margin.bottom); // Use the min-height from the container
      
      // Ensure minimum dimensions
      width = Math.max(width, 100);
      height = Math.max(height, 100);

      // Fix 3: Set the SVG viewBox to ensure proper scaling
      chartContainer
        .attr("viewBox", `0 0 ${rect.width} 200`)
        .attr("preserveAspectRatio", "xMidYMid meet");


      var minWidth=1;
      var timeextent=d3.extent(data, function(d) { return d.datetimeInstance; });

      var periodCount=data.length; //me.isHourly?d3.timeHour.count(timeextent[0],timeextent[1]):d3.timeDay.count(timeextent[0],timeextent[1]);
      console.log("periodCount3="+periodCount);
      if (!periodCount) {
         return;
      }
      var periodWidth=width/(periodCount+1);
      if (periodWidth<minWidth) periodWidth=minWidth;
      var summaryPeriodInterval=Math.round(minWidth/periodWidth);
      //if the forecast periods (days or hours) will be too small, set them to a minimum size
      //in this case the periods will be draggable to view periods out of view
      //if (periodWidth<minWidth) {
      //    periodWidth=minWidth;
     // }

      width=periodWidth*(periodCount);
      //chartContainer.style("width",(width+periodWidth)+"px")

      container.selectAll(".period")
        .style("visibility", function (d,i) {
          return (i%summaryPeriodInterval===0)?"visible":"hidden";
        })
        .style("left",  function (d,i) {
          return (i*periodWidth)+"px";
        })
        .style("width", periodWidth+"px")
      //set up the x axis representing time
      var x = d3.scaleTime().range([0, width]);
      x.domain(timeextent);
      
      var chartDrawing;
      if (chartContainer.select("g").empty()) {
          var chartRoot=chartContainer; 

          chartRoot.append("clipPath")
              .attr("id", "clippath")   
              .append("rect")
                  .attr("x",0)
                  .attr("y",0)
                  .attr("width", width + margin.left + margin.right)
                  .attr("height", height + margin.top + margin.bottom);


          chartRoot=chartRoot.append("g")
              .attr("transform",
                  "translate(" + margin.left + "," + margin.top + ")")
              .attr("clip-path","url(#clippath)")

          
          chartDrawing=chartRoot.append("g")
              .attr("class", "container")
          
          chartDrawing.append("path")
              .attr("class", "precipprobline");

          chartDrawing.append("path")
              .attr("class", "line")
          /*
          chartDrawing.append("path")
              .attr("class", "line2")
          */


          /*
          chartDrawing.append("path")
              .attr("class", "precipline")

          chartDrawing.append("path")
              .attr("class", "precipprobline")
          
          chartDrawing.append("g")
              .attr("class", 'bars')


          */
          chartDrawing.append("linearGradient")
              .attr("id", "reflectivity-gradient")
              .attr("gradientUnits", "userSpaceOnUse")


              
          chartDrawing.append("g")
            .attr("class", 'precipremote-bars');

          let timeannotations=chartDrawing.append("g")
              .attr("class", "timeannotations")

          timeannotations.append("line")
              .attr("class", "nowline")

           timeannotations.append("line")
              .attr("class", "maptimeline")

          //future.append("rect")

        
          chartDrawing.append("g")			
              .attr("class", "grid")

          chartDrawing.append("g")			
              .attr("class", "y1 axis")
              .attr("transform", "translate(0, 0)")

           chartDrawing.append("g")			
              .attr("class", "y2 axis")
              .attr("transform", "translate(" + (width) + ", 0)")  // move to the right edge

      } else {
          chartDrawing=chartContainer.select(".container");
      }

      chartContainer.select("#clippath rect")
          .attr("x",-margin.left)
          .attr("width", (width+periodWidth))
          .attr("height", height + margin.top + margin.bottom);

      chartDrawing.attr("transform", null);

      //set up vertical y scales for the temperature and precipitation fields
     // var y = d3.scaleLinear().range([height, 0]);
      
      //y.domain([0, d3.max(data, function(d) { return Math.max(d.reflectivity, d.reflectivity2)+5 })]);
      
       // set up vertical y scale using logarithmic scale
      //var y = d3.scaleLog().range([height, 0]);
      const minReflectivityValue=5;
      var y = d3.scaleLinear()
        .domain([minReflectivityValue, 75])
        .range([height, 0]); // Extend the range above 0 to shift everything down

      var yPrecipRemote = d3.scaleLinear()
        .range([height, height * 0.6])
        .domain([0, Math.max(0.01, d3.max(data, d => (d.precip || d.precipremote || 0)) * 1.1)]);


      var yprecipprob = d3.scaleLinear().range([height, 5]).domain([0, 100]);
      //area creation function for the temperature 

      let preciprobline = d3.area()
          .x(function(d, i) {
             return x(d3.timeMinute.offset( d.datetimeInstance,7.5));
          })
          .y0(height)
          .y1(function(d) { 
            return yprecipprob(d.precipprob || 0);
          })
          .curve(d3.curveCatmullRom);

      let reflectivityarea = d3.area()
          .x(function(d, i) {
             return x(d3.timeMinute.offset( d.datetimeInstance,7.5));
          })
          .y0(height)
          .y1(function(d) { 
            return y(d.reflectivity>minReflectivityValue ? d.reflectivity : minReflectivityValue);
          })
          .curve(d3.curveCatmullRom);
    

          /*
      let reflectivity2area=d3.area()
           
              .x(function(d, i) {
                return x(d3.timeMinute.offset( d.datetimeInstance,7.5));
              })
              .y0(height)
              .y1(function(d) { 
                return y(d.reflectivity2 || 1e-3); 
              })
              .curve(d3.curveCatmullRom) ;
          */
      
      
      //create a gradient that maps the colors to particular temperature
      //in this case we will use the turbo colors from d3 and interpolate them between 255 and 311K (approx 0-100F)
      var colorStopCount=12;


      var minReflectivity=y.domain()[0], maxReflectivity=y.domain()[1];

      var reflectivityInterval= (100-0)/(colorStopCount+1);
      var colors=VcWidgetHelper.dbzColorIntervals();
      var barWidth = width / data.length * 0.8;
  
      //create the gradient stops based on the above stop array
      var stopsContainer = chartDrawing.select("#reflectivity-gradient")
        .attr("x1", 0)
        .attr("y1", height)  // Bottom of chart
        .attr("x2", 0)
        .attr("y2", 0)       // Top of chart (changed from y(100))
        .selectAll("stop")
        .data(colors, function(d) {
            return d.color
        });
      stopsContainer.exit().remove();
      stopsContainer=stopsContainer.merge(stopsContainer.enter().append("stop"))
      
          stopsContainer.attr("offset", function(d) { 
            return d.offset; 
          })
            .attr("stop-color", function(d) { 
              return d.color; 
            })
              //.attr("stop-opacity", function(d,i) { return 0.5+0.5*i/colors.length; });
      


      //populate the area chart for the temperature view
      chartDrawing.select(".line")
          .data([data])
          //.transition()
          .attr("d", reflectivityarea);

 /*
      chartDrawing.select(".line2")
          .data([data])
          //.transition()
          .attr("d", reflectivity2area);


     
      chartDrawing.select(".precipline")
          .data([data])
          //.transition()
          .attr("d", preciparea);
      */
      chartDrawing.select(".precipprobline")
          .data([data])
          //.transition()
          .attr("d", preciprobline);
      

      chartDrawing.select(".precipremote-bars")
        .selectAll("rect")
        .data(data.filter(d => (d.precip || d.precipremote) && (d.precip > 0 || d.precipremote > 0))) // Filter out zero/null values
        .join("rect")
          .attr("x", (d, i) => x(d.datetimeInstance) - barWidth/2)
          .attr("y", d => yPrecipRemote(d.precip || d.precipremote || 0))
          .attr("width", barWidth)
          .attr("height", d => height - yPrecipRemote(d.precip || d.precipremote || 0))
          .attr("fill", "#4a90e2")
          .attr("fill-opacity", 0.6)
          .attr("stroke", "#2c5aa0")
          .attr("stroke-width", 0.5);

      const hasAnyPrecip = data.some(d => (d.precip || d.precipremote) && (d.precip > 0 || d.precipremote > 0));
      chartDrawing.select(".y-precipremote").remove();
      if (hasAnyPrecip) {
        // Remove existing axis first
      
        
        // Add new axis
        chartDrawing.append("g")
          .attr("class", "y-precipremote axis")
          .attr("transform", "translate(15, 0)")
          .call(d3.axisLeft(yPrecipRemote)
            .ticks(3)
            .tickFormat(d => {
              if (d === 0) return ""; // Don't show 0
              // Use appropriate precision based on value
              if (d < 0.01) return d.toFixed(3);
              if (d < 0.1) return d.toFixed(2);
              if (d < 1) return d.toFixed(1);
              return d.toFixed(0);
            })
          );
      }

      //draw vertical lines at the boundaries of each period
      
      chartDrawing.select(".grid")
          .attr("transform", "translate(0," + height + ")")
          .call(d3.axisBottom(x)
             .ticks(d3.timeHour.every(width>1000?1:3))
             // .ticks(periodCount)
              .tickSize(-10)
             // .tickFormat(d3.timeFormat("%H"))
          )
      

          /*
      chartDrawing.select(".grid")
          .attr("transform", "translate(0," + height + ")")
          .call(d3.axisBottom(x)
            .ticks(d3.timeHour.every(width > 1000 ? 1 : 3))
            .tickSize(-10)
            .tickFormat(d => {
              const diffHours = Math.round((d - now) / (1000 * 60 * 60));
              return `+${diffHours}h`;
            })
          );

 */

      let nowx=x(currentLocal);

      /*
      chartDrawing.select(".future")
         .attr("transform", "translate("+nowx+",0)")
         .select("rect")
          .attr("width", (width-nowx))
          .attr("height", height);
      */
      /*
      const y1Labels = ["", "Light", "Mod", "Heavy - Intensity"];

      const y1axis = d3.scalePoint()
          .domain(y1Labels)
          .range([height, 0])
          .padding(0.5);  // Optional: add space above/below first/last label

      chartDrawing.select(".y1")
        .call(d3.axisRight(y1axis)
            .tickSize(0)           // No tick lines
            .tickPadding(8)        // Space between axis and label
        )
        .call(g => g.selectAll(".domain").remove()); // Remove the axis line
            */

      const y2Labels = ["", "25 ", "50 ", "75 ", "% Chance - 100"];

      const y2axis = d3.scalePoint()
          .domain(y2Labels)
          .range([height, 5])
          .padding(0);  // Optional: add space above/below first/last label

      chartDrawing.select(".y2")
        .call(d3.axisLeft(y2axis)
            .tickSize(0)           // No tick lines
            .tickPadding(8)        // Space between axis and label
        )
        .call(g => g.selectAll(".domain").remove()); // Remove the axis line

      chartDrawing.select(".timeannotations .nowline")
        .attr("x1", nowx)  
        .attr("y1", 20)
        .attr("x2", nowx)  
        .attr("y2", height);

      me.updateMapTimeLine=function(datetime) {
        datetime=this.toLocalDateInstance(datetime.getTime()/1000);
        let datetimex=x(datetime);
        chartDrawing.select(".timeannotations .maptimeline")
        .attr("x1", datetimex)  
        .attr("y1", 20)
        .attr("x2", datetimex)  
        .attr("y2", height);


      }
      if (me.mapDateTime) me.updateMapTimeLine(me.mapDateTime);
    },
    
    formatTimeAgo:function(minutes, prefix, fallback)  {
      const mins = Number(minutes); // force conversion to primitive number

      if (isNaN(mins)) return fallback || "invalid time";
      //if (typeof minutes !== "number" || isNaN(minutes)) return fallback || "invalid time";

      if (minutes < 10) return fallback || `${prefix}any moment`;

      if (minutes >= 1440) {
        const days = Math.floor(minutes / 1440);
        return `${String(prefix)}${days} day${days > 1 ? 's' : ''}`;
      } else if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        return `${String(prefix)}${hours} hour${hours > 1 ? 's' : ''}`;
      } else {
        return `${String(prefix)}${minutes} minute${minutes !== 1 ? 's' : ''}`;
      }
    },

    getRainDescription:function(reflectivity) {
      if (reflectivity > 50) return "Torrential rain";
      if (reflectivity > 40) return "Heavy rain";
      if (reflectivity > 20) return "Moderate rain";
      if (reflectivity > 10) return "Light rain";
      if (reflectivity > 0) return "Light rain or drizzle";
      return null;
    },
    getMinuteAtTime(datetime) {
      var me = this;
      if (!me.minutely.length) return null;

      const epoch = datetime.getTime() / 1000;

      return  me.minutely.reduce((a, d) => {
              if (!a && d.datetimeEpoch > epoch) return d;
              return a;
            }, null);
    }
  },
  computed:{
    
    adjustedLocation() {
      return VcWidgetHelper.resolveLocation(this.wxdata, this.location, this.name, );
    },
    hasError:function() {
      return this.error;
    },
    hasData:function() {
      return this.focusDays;
    },
    currentConditions() {
      var me=this;
      return me.wxdata && me.wxdata.currentConditions;
    },
    showCurrent:function() {
      return this.currentConditions;
    },
    showPeriods:function() {
      return this.dayIndex>=0;
    },
    /*
    days1:function() {
      let me=this;
      return me.days && me.days.length>=1 && me.days.slice(0,1);
    },
    */
    daysShortTerm:function() {
      let me=this;
      let today = me.today;
      if (!today || !me.days) return null;
      let todayIndex=me.days.indexOf(today);
      if (todayIndex<0) todayIndex=0;
      return me.days && me.days.length>=1 && me.days.slice(todayIndex,todayIndex+1);
    },
    
    daysWeekend:function() {
      let me=this;
      return me.days && me.days.length>=3 && me.days.filter((d, i)=> 
        {
          let dow=new Date(d.datetimeEpoch*1000).getDay()
          return i<6 && (dow===0 || dow===6);
        });
    },


    days:function() {
      var me=this;
      if (!me.wxdata || !me.wxdata.days || !me.wxdata.days.length) return null;
      return me.wxdata.days
    },
    
    isForecastNextDays() {
      var me = this;
      let today = me.today;
      if (!today || !me.days) return false;
      let todayIndex=me.days.indexOf(today);
      return (todayIndex >= 0 && todayIndex < me.days.length-1);
    },

    nextDays: function() {
      var me = this;
      if (!me.wxdata || !me.wxdata.days || !me.wxdata.days.length) return null;
      let startDay = null;
      if (me.daysShortTerm) {
        startDay = me.daysShortTerm[me.daysShortTerm.length - 1];
      } else {
        startDay = me.today;
      }

     
      if (startDay != null) {
        return me.wxdata.days.filter(day => day.datetimeEpoch > startDay.datetimeEpoch);
      }

      return me.wxdata.days;
    },

    focusDays:function() {
      var me=this;
  
      if (!me.wxdata || !me.wxdata.days) return null;
      if (me.dayIndex || me.dayIndex===0) {
        if (me.endDayIndex>me.dayIndex) {
           return me.wxdata.days.slice(me.dayIndex, me.endDayIndex+1)
        } else {
           return [me.wxdata.days[me.dayIndex]];
        }
      }
      return me.wxdata.days;
    },
    showAlerts:function() {
      return this.alerts && this.alerts.length!==0;
    },
    alerts:function() {
        var me=this;
        return me.wxdata && me.wxdata.alerts;
    },
    minutely:function() {
        var me=this;
        let minutes=[];
        if (!me.wxdata || !me.wxdata.days) return [];
        let nowEpoch=new Date().getTime()/1000;
        let startTime=me.today?nowEpoch-6*3600:me.wxdata.days[0].datetimeEpoch;
        let endTime=me.today?nowEpoch+12*3600:me.wxdata.days[0].datetimeEpoch+24*3600;
        me.wxdata.days.forEach(day=> {
          day.hours && day.hours.forEach((hour)=>{
            hour.minutes &&  hour.minutes.forEach((minute)=>{
              if (minute && minute.datetimeEpoch>=startTime && minute.datetimeEpoch<=endTime ) {
                minute.precipprob=hour.precipprob;
                minutes.push(minute);
              }
            });
          })
        });
        return minutes;

    },

    today:function() {
      var me=this;

      return me.wxdata && me.wxdata.days && me.wxdata.days.reduce((a,d)=> {
        if  (VcWidgetHelper.isToday(d.datetimeEpoch, me.wxdata.timezone))  return d;
        return a;
      }, null);

    },

    yesterday:function() {
      var me=this;
      return me.wxdata && me.wxdata.days && me.wxdata.days.reduce((a,d)=> {
        if  (VcWidgetHelper.isYesterday(d.datetimeEpoch, me.wxdata.timezone))  return d;
        return a;
      }, null);
    },

    rainInVicinity:function() {
      var me=this;
      let lastNextRain=me.lastNextRain;
      return lastNextRain && (lastNextRain.latest || lastNextRain.next);
    },
   
    nowEpoch:function() {
      var me=this;
      let lastNextRain=me.lastNextRain;
      return lastNextRain && lastNextRain.nowEpoch;
    },
    lastNextRain: function () {
      var me = this;
      if (!me.minutely.length) return null;

      const nowEpoch = new Date().getTime() / 1000;
      
      // Helper function to find the hour for a minute
      const findHourForMinute = (minute) => {
        if (!minute || !me.wxdata || !me.wxdata.days) return null;
        
        for (let day of me.wxdata.days) {
          if (!day.hours) continue;
          for (let hour of day.hours) {
            if (!hour.minutes) continue;
            for (let min of hour.minutes) {
              if (min.datetimeEpoch === minute.datetimeEpoch) {
                return hour;
              }
            }
          }
        }
        return null;
      };

      // Find latest precipitation before now
      let latestPrecip = me.minutely.reduce((a, d) => {
        if (d.datetimeEpoch > nowEpoch) return a;
        if (d.reflectivity > 0) return d;
        return a;
      }, null);

      // Find next precipitation after now
      let nextPrecip = null;
      let foundLight = null;
      let norainPeriods = 0;
      
      for (let d of me.minutely) {
        if (d.datetimeEpoch < nowEpoch) continue;

        if (d.reflectivity <= 0) {
          norainPeriods++;
          if (foundLight && norainPeriods > 3) {
            nextPrecip = foundLight;
            break;
          }
          continue;
        }
        
        norainPeriods = 0;
        if (!nextPrecip && d.reflectivity > 20) {
          nextPrecip = d;
          break;
        }
        
        if (!foundLight && d.reflectivity <= 20) {
          foundLight = d;
        }
      }

      // Find max future precipitation
      let maxFuturePrecip = me.minutely.reduce((a, d) => {
        if (d.datetimeEpoch < nowEpoch) return a;
        if (d.reflectivity > 0 && (!a || d.reflectivity > a.reflectivity)) return d;
        return a;
      }, null);

      // Enhance with hour data and time calculations
      if (latestPrecip) {
        latestPrecip.minsAgo = Math.round((nowEpoch - latestPrecip.datetimeEpoch) / 60);
        latestPrecip.hour = findHourForMinute(latestPrecip);
      }

      if (nextPrecip) {
        nextPrecip.minsUntil = Math.round((nextPrecip.datetimeEpoch - nowEpoch) / 60);
        nextPrecip.hour = findHourForMinute(nextPrecip);
      }

      if (maxFuturePrecip) {
        maxFuturePrecip.minsUntil = Math.round((maxFuturePrecip.datetimeEpoch - nowEpoch) / 60);
        maxFuturePrecip.hour = findHourForMinute(maxFuturePrecip);
      }

      return {
        latest: latestPrecip,
        next: nextPrecip,
        maxFuture: maxFuturePrecip,
        nowEpoch: nowEpoch,
      };
    },
    mapRainRate:function() {
      var me=this;
      let dateTimeMinute=me.mapDateTime && me.getMinuteAtTime(me.mapDateTime);
      if (!dateTimeMinute) return "";

      if (dateTimeMinute.reflectivity > 0) {
        return dateTimeMinute.datetime+": "+dateTimeMinute.reflectivity+"dBz "+VcWidgetHelper.dbzToRainRate(dateTimeMinute.reflectivity, me.unitGroup);
      }
      return dateTimeMinute.datetime+": No rain";
    },
    nearbyRainWithNone:function() {
      var me=this;
      return me.nearbyRain || "No rain nearby";
    },
    nearbyRain: function() {
      var me = this;
      let lastNextRain = me.lastNextRain;
      if (!lastNextRain || (!lastNextRain.latest && !lastNextRain.next)) return null;
      
      if (lastNextRain.latest && !lastNextRain.next) return me.lastRain;
      if (!lastNextRain.latest && lastNextRain.next) return me.nextRain;

      // Handle ongoing precipitation
      if (lastNextRain.latest.minsAgo < 15 && lastNextRain.next.minsUntil < 15) {
        let desc = me.getRainDescription(lastNextRain.latest.reflectivity) + " continuing";
        
        // Add duration if we can calculate it
        if (lastNextRain.next.hour && lastNextRain.latest.hour) {
          const totalDuration = lastNextRain.latest.minsAgo + lastNextRain.next.minsUntil;
          if (totalDuration > 30) {
            desc += ` for at least ${me.formatTimeAgo(totalDuration, "", null)}`;
          }
        }
        
        return desc;
      } else if (lastNextRain.latest && lastNextRain.latest.hour) {
        const bestCloudcover = lastNextRain.latest.cloudcover ? lastNextRain.latest : lastNextRain.latest.hour;
        const bestHumidity = lastNextRain.latest.humidity ? lastNextRain.latest : lastNextRain.latest.hour;
        const bestVisibility = lastNextRain.latest.visibility ? lastNextRain.latest : lastNextRain.latest.hour;

        if (bestCloudcover.cloudcover > 90 && bestHumidity.humidity > 90 && bestVisibility.visibility < 8) {
          var mizzle = `Possible fog/mist/mizzle/drizzle  ${me.formattedValue(bestCloudcover, 'cloudcover')}%, ${me.formattedValue(bestHumidity, 'humidity')}% humidity, visibility ${me.formattedValue(bestVisibility, 'visibility')}  `;
          return me.lastRain + ". <br/><br/>"+mizzle;
        }
        
      }

      return me.lastRain + ". <br/><br/>" + me.nextRain;
    },


    lastRain: function() {
      var me = this;
      const latest = this.lastNextRain?.latest;
      if (!latest) return "No recent precip";

      const period = latest.minsAgo < 10
        ? "currently falling"
        : this.formatTimeAgo(latest.minsAgo, "", null) + " ago";

      const desc = this.getRainDescription(latest.reflectivity);
      if (desc) {
        const rate = VcWidgetHelper.dbzToRainRate(latest.reflectivity, this.unitGroup);
        let output = `${desc} ${period} (${rate})`;
        
        // Add hour-level information if available
        if (latest.hour) {
          if (latest.hour.windspeed > 20) {
            output += ` with ${this.formattedValue(latest.hour, 'windspeed')} winds`;
          }
          if (latest.hour.conditions) {
            output += ` - ${latest.hour.conditions}`;
          }
        }
        
        return output;
      }

      return "No recent precip";
    },

    nextRain: function() {
      const next = this.lastNextRain?.next;
      const max = this.lastNextRain?.maxFuture;

      if (!next) return "No upcoming precip";

      const period = this.formatTimeAgo(next.minsUntil, "in ", "any moment");

      const desc = this.getRainDescription(next.reflectivity);
      if (!desc) return "No upcoming precip";

      let output = `${desc} ${period} (${VcWidgetHelper.dbzToRainRate(next.reflectivity, this.unitGroup)})`;

      // Add hour-level context
      if (next.hour) {
        // Add temperature info if significantly different from current
        if (this.currentConditions && Math.abs(next.hour.temp - this.currentConditions.temp) > 5) {
          const tempChange = next.hour.temp > this.currentConditions.temp ? "warmer" : "cooler";
          output += `, ${tempChange} at ${this.formattedValue(next.hour, 'temp')}`;
        }
        
        // Add wind info if significant
        if (next.hour.windspeed > 15) {
          output += ` with ${this.formattedValue(next.hour, 'windspeed')} winds`;
        }
        
        // Add precipitation type if different
        if (next.hour.preciptype && next.hour.preciptype.length) {
          const precipType = next.hour.preciptype.join(', ');
          if (precipType !== 'rain') {
            output += ` (${precipType})`;
          }
        }
      }

      // Handle intensity changes
      if (max && max.reflectivity !== next.reflectivity) {
        const change = VcWidgetHelper.dbzToRainRate(max.reflectivity, this.unitGroup);
        const timeDiff = (max.datetimeEpoch - next.datetimeEpoch) / 60; // in minutes
        
        if (timeDiff > 60) {
          const intensityChange = max.reflectivity > next.reflectivity * 2 ? "a lot heavier"
            : max.reflectivity > next.reflectivity ? "heavier"
            : "more rain";
          output += `. ${intensityChange.charAt(0).toUpperCase() + intensityChange.slice(1)} (${change}) in ${this.formatTimeAgo(timeDiff, "", null)}`;
          
          // Add max hour context
          if (max.hour && max.hour.conditions) {
            output += ` - ${max.hour.conditions}`;
          }
        } else {
          const intensityChange = max.reflectivity > next.reflectivity * 2 ? "a lot heavier"
            : max.reflectivity > next.reflectivity ? "heavier"
            : "lighter";
          output += ` becoming ${intensityChange} (${change})`;
        }
      }

      return output;
    },

    summarykpicount:function() {
        return this.summarykpis?this.summarykpis.length:0;
    },
    showSummary() {
      return this.doShow('icon') || this.summarykpicount
    },
    summaryCssClasses() {
      if (this.doShow('icon')) return 'summarykpis'+this.summarykpicount
      return 'summarykpis'+this.summarykpicount+" noicon"; 
    },



  },
  watch: {
    /*
    newLocation:{
        handler:function(newValue,oldValue) {
          var me=this;
          if (!newValue) return;
          me.location=newValue;
          me.refreshData(true);
        },
        deep: true
    },*/
    config:{
        handler:function(newValue,oldValue) {
          var me=this;
          var containerElement=me.$el.parentElement;
          VcWidgetHelper.readElementConfig(containerElement, me);
          me.refreshData(true);
        },
        deep: true
    },
  },
  mounted() {
    var me=this;
    var containerElement=me.$el.parentElement;
    VcWidgetHelper.readElementConfig(containerElement, me);

    me.renderMap();
    me.updateMap();
    me.renderCharts();
    me.refreshData();
    
    me.onresize=VcWidgetHelper.debounce(function () {
        me.renderCharts();
    }, 250);

    window.addEventListener("resize", me.onresize);
   
  },
   components: {
    DayWidget,
    //LocationEditor
  }
}
</script>

<style scoped>

.expanded .fs-xs {
   /*font-size:1rem  !important;*/
}

.expanded {
  margin:10px 0px 10px 0px !important;
  font-size:1.5em  !important;
}
.expanded .expandable {
  display:block !important;
}

.weatherMap {
   /* 
    min-height:50vh; */
    height:100%;
   
}

.fullscreen {
  height: 100svh  !important; /*quick hack because 100% hidden by mobile browser toolbar */
  width: 100vw !important;
  position: fixed;
  top: 0vh;
  right: 0;
  left: 0;
  z-index: 1030;
  margin: 0 !important;
  padding: 0px;
  background: #ffffffd1;
}
:deep() .mapexpand {
  display:block;
}
:deep() .mapcollapse {
  display:none;
}
.fullscreen :deep() .mapexpand {
  display:none;
}

.fullscreen :deep() .mapcollapse {
  display:block;
}

:deep() .mapplay {
  display:block;
}
:deep() .mappause {
  display:none;
}
.isplaying :deep() .mapplay {
  display:none;
}

.isplaying :deep() .mappause {
  display:block;
}

:deep() .icon  > div {
    height: 24px;
    width: 24px;
    background-size: 24px 24px;
    overflow: hidden;
}





  .chartcontainer svg {
    flex:1;
  }

  .chartcontainer :deep() .axis  {
    font-size:0.8em;
  }

  .chartcontainer :deep() .axis line {
    stroke: lightgrey;
    stroke-opacity: 0.7;
    shape-rendering: crispEdges;
  }
  .chartcontainer :deep() .axis  path {
    stroke-width: 0;
  }

  .chartcontainer {
    position: relative;
    min-height: 200px;
    height: 200px;
    font-size: 0.8em;
    overflow: visible; /* Allow the SVG to show fully */
}

  .chartcontainer .chart {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
           height: 100%;
      overflow: visible;
  }
  .chartcontainer .chart :deep()  .line {
      fill: url(#reflectivity-gradient);
      stroke-width: 05px;
  }
  .chartcontainer .chart :deep()  .line2 {
      fill: url(#reflectivity-gradient);
      fill-opacity: 0.2;
      stroke: rgb(68, 68, 68);
      stroke-width: 0.5px;
      stroke-opacity: 0.5;
      

  }
  .chartcontainer .chart :deep()  .precipline {
      fill: #045a8d;
     stroke-width: 2px;
  }
  .chartcontainer .chart :deep()  .precipprobline {
    fill: rgb(225 224 224);
    stroke: rgb(180 180 180);
    stroke-width: 1px;
  }
  .chartcontainer .chart :deep() .grid line {
    stroke: darkgray;
    shape-rendering: crispEdges;
  }

  .chartcontainer .periods {
    position: absolute;
    -webkit-user-select: none;
    -ms-user-select: none;
    user-select: none;
    display:flex;
    flex-direction: row;
    height: 100%;
  }
  .chartcontainer .periods .period {
    position: absolute;
    display:flex;
    flex-direction: column;
    justify-content: flex-start;
    grid-template-columns: 50% 50%;
    align-items: center;
    justify-items: center;
    /* grid-gap: 1px; */
    cursor: grab;
    height: 100%;
    color:#8e8b8b;
    font-size: 1.1em;
    white-space: nowrap;
    cursor:pointer;
  }

  .chartcontainer .periods .period .date {
    font-size: 0.8em;
  }
  .chartcontainer .periods .period .dow {
    font-weight:bold;
  }
  .chartcontainer .periods .period .time {
   
  }
  .chartcontainer .periods .period.hour {
    font-size: 0.8em;
    cursor:default;
  }
		

  .chartcontainer .periods .period.conditions {
    display:none;
    color:#8e8b8b;
    font-size: 0.9em;
  }

  .chartcontainer .periods .period  .maxt {
    font-weight: bold;
  }
  .chartcontainer .periods .period  .mint {
    font-size: 0.9em;
    color:#8e8b8b;
  }
  
  .chartcontainer .periods .period  .hidden {
    visibility: hidden;
    display:none;
  }
  .chartcontainer .periods .period .precip {
    margin-top: auto;
    color: #163753;
    font-size: 0.9em;
  }
  .chartcontainer .chart :deep()  .future  {
      fill: #ebeaea;
      stroke-width: 2px;
  }
  .chartcontainer .chart :deep()  .nowline {
    stroke: #e33939;
  }

  .chartcontainer .chart :deep()  .maptimeline {
    stroke: #302f2f;
  }

  .chartcontainer .chart :deep()  .future .title {
    text-anchor: end;
    alignment-baseline: hanging;
    font-size: 0.6em;
  }
  .chartcontainer .chart :deep() .grid .tick line {
    stroke: #ccc;
    stroke-width: 0.5px;
  }

  .chartcontainer .chart :deep() .grid .domain {
    stroke: #ccc;
    stroke-width: 0.5px;
  }
    
  :deep(.leaflet-control) {
  z-index: 800 !important; /* Default is 1000 */
}

  /* Or target specific controls */
  :deep(.leaflet-control-zoom) {
    z-index: 800 !important;
  }

  :deep(.leaflet-control-attribution) {
    z-index: 800 !important;
  }

  :deep(.leaflet-control-layers) {
    z-index: 800 !important;
  }

  /* You can also target the control container */
  :deep(.leaflet-control-container) {
    z-index: 800 !important;
  }

  /* For the entire pane system */
  :deep(.leaflet-pane) {
    z-index: 400 !important; /* Default is 400-700 range */
  }

  :deep(.leaflet-control-container .leaflet-top,
      .leaflet-control-container .leaflet-bottom) {
    z-index: 800 !important;
  }

  .mh-sm-50 { min-height: 40vh; }

  /* Small devices (landscape phones, 576px and up) */
  @media (min-width: 770px) {
    .mh-sm-50 { min-height: 10vh; }
   
}
</style>


